import React, { useState, useEffect, useRef } from 'react';
import { getAllPlaceholderTags, insertFloatingPlaceholderAtMatch } from '../services/wordHelper';
import { getAuthHeaders, API_URL } from '../services/auth';

interface Suggestion {
  id: string;
  originalText: string;
  suggestedKey: string;
  suggestedLabel: string;
  suggestedType: string;
  confidence: number;
  reason: string;
  context: string;
  existingMatch: boolean;
  status: 'pending' | 'accepted' | 'rejected';
  editing: boolean;
}

type Step = 'analyze' | 'review' | 'applying' | 'done';

export function ArtemisGeneration({ onTemplateSaved }: { onTemplateSaved?: () => void }) {
  const [step, setStep] = useState<Step>('analyze');
  const [analyzing, setAnalyzing] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [applyProgress, setApplyProgress] = useState(0);
  const [applyTotal, setApplyTotal] = useState(0);
  const hasRun = useRef(false);

  // Auto-analyze on mount
  useEffect(() => {
    if (!hasRun.current) {
      hasRun.current = true;
      analyzeDocument();
    }
  }, []);

  const analyzeDocument = async () => {
    setAnalyzing(true);
    setError(null);
    setSuggestions([]);

    try {
      // Extract text paragraph-by-paragraph so real line breaks survive.
      // body.text has no separators, which can make distinct lines merge
      // (e.g. a heading blending into the line below).
      let documentText = '';
      let documentTitle = '';
      try {
        await Word.run(async (context) => {
          const paragraphs = context.document.body.paragraphs;
          const props = context.document.properties;
          paragraphs.load('text');
          props.load('title');
          await context.sync();
          documentText = paragraphs.items.map(p => p.text).join('\n');
          documentTitle = props.title || '';
        });
      } catch { /* ignore */ }

      if (!documentText || documentText.trim().length < 20) {
        setError('Document is empty or too short to analyze. Please open a document first.');
        setStep('analyze');
        return;
      }

      // Collect tags already placed in the doc (content controls + floating
      // shape tokens) so Artemis can skip them — body.text doesn't include
      // floating-shape content, so Claude would otherwise re-suggest them.
      let alreadyPlaced: string[] = [];
      try {
        const placed = await getAllPlaceholderTags();
        alreadyPlaced = Array.from(new Set(placed.map(p => p.tag)));
      } catch { /* ignore */ }

      const resp = await fetch(`${API_URL}/ai/analyze-document`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ documentText, documentTitle, alreadyPlaced }),
      });

      const data = await resp.json();
      if (!data.success) throw new Error(data.error?.message || 'Analysis failed');

      const items: Suggestion[] = (data.data?.suggestions || []).map((s: any) => ({
        ...s,
        status: 'pending' as const,
        editing: false,
      }));

      if (items.length === 0) {
        setError('No placeholders detected. This document may already be a template or contains no dynamic fields.');
        setStep('analyze');
        return;
      }

      setSuggestions(items);
      setStep('review');
    } catch (err: any) {
      setError(err?.message || 'Analysis failed. Please try again.');
      setStep('analyze');
    } finally {
      setAnalyzing(false);
    }
  };

  const updateSuggestion = (id: string, updates: Partial<Suggestion>) => {
    setSuggestions(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
  };

  // Accept and immediately place the placeholder in the document as a
  // floating shape. Finds the matched text and drops a chip in the host
  // paragraph (chip overlays the match via wrap:none; match text is kept).
  const acceptAndPlace = async (s: Suggestion) => {
    updateSuggestion(s.id, { status: 'accepted' });
    try {
      const placed = await insertFloatingPlaceholderAtMatch(
        s.originalText,
        s.suggestedKey,
        s.suggestedLabel,
        s.suggestedType,
      );
      if (!placed) {
        throw new Error(`Couldn't find "${s.originalText.slice(0, 40)}" in the document.`);
      }

      // Register in catalog if new
      if (!s.existingMatch) {
        const resp = await fetch(`${API_URL}/placeholders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            key: s.suggestedKey, label: s.suggestedLabel, type: s.suggestedType,
            source: 'addin',
            description: s.reason, required: s.confidence >= 0.8,
            sampleValue: s.originalText,
          }),
        });
        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          const msg = body?.error?.message || `HTTP ${resp.status}`;
          // Don't throw on "already exists" — that's benign
          if (!/already exists/i.test(msg)) {
            throw new Error(`Failed to register '${s.suggestedKey}': ${msg}`);
          }
        }
      }
    } catch (err: any) {
      console.warn(`Failed to place ${s.suggestedKey}:`, err);
      setError(err?.message || `Failed to register '${s.suggestedKey}'`);
    }
  };

  const acceptAll = async () => {
    for (const s of suggestions.filter(s => s.status === 'pending')) {
      await acceptAndPlace(s);
    }
  };

  const rejectAll = () => {
    setSuggestions(prev => prev.map(s => s.status === 'pending' ? { ...s, status: 'rejected' } : s));
  };

  const accepted = suggestions.filter(s => s.status === 'accepted');
  const pending = suggestions.filter(s => s.status === 'pending');
  const rejected = suggestions.filter(s => s.status === 'rejected');

  // Apply accepted placeholders to the document
  const applyPlaceholders = async () => {
    const toApply = accepted;
    if (toApply.length === 0) return;

    setStep('applying');
    setApplyTotal(toApply.length);
    setApplyProgress(0);

    try {
      for (let i = 0; i < toApply.length; i++) {
        const s = toApply[i];
        setApplyProgress(i + 1);

        try {
          await Word.run(async (context) => {
            const body = context.document.body;
            const results = body.search(s.originalText, { matchCase: true, matchWholeWord: false });
            results.load('items');
            await context.sync();

            if (results.items.length > 0) {
              // Replace first occurrence with a content control
              const range = results.items[0];
              const cc = range.insertContentControl();
              cc.tag = s.suggestedKey;
              cc.title = s.suggestedLabel;
              cc.appearance = 'BoundingBox';
              cc.color = '#0d9488';
              // Set placeholder text
              cc.insertText(`{${s.suggestedKey}}`, 'Replace');
              await context.sync();
            }
          });
        } catch (err) {
          console.warn(`Failed to apply placeholder ${s.suggestedKey}:`, err);
        }
      }

      // Register only NEW placeholders in the catalog (skip existing ones)
      const newPlaceholders = toApply.filter(s => !s.existingMatch);
      const failures: string[] = [];
      for (const s of newPlaceholders) {
        const resp = await fetch(`${API_URL}/placeholders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            key: s.suggestedKey,
            label: s.suggestedLabel,
            type: s.suggestedType,
            source: 'addin',
            description: s.reason,
            required: s.confidence >= 0.8,
          }),
        }).catch((err) => ({ ok: false, json: async () => ({ error: { message: err?.message || 'network error' } }) } as unknown as Response));
        if (!resp.ok) {
          const body = await resp.json().catch(() => ({} as any));
          const msg = body?.error?.message || `HTTP ${resp.status}`;
          if (!/already exists/i.test(msg)) failures.push(`${s.suggestedKey}: ${msg}`);
        }
      }
      if (failures.length > 0) {
        setError(`Registered placeholders with ${failures.length} failure(s). First: ${failures[0]}`);
      }

      setStep('done');
    } catch (err: any) {
      setError(err?.message || 'Failed to apply placeholders');
      setStep('review');
    }
  };

  // ── Render ──

  const typeColors: Record<string, { bg: string; text: string }> = {
    Text: { bg: '#e0f2fe', text: '#0369a1' },
    Date: { bg: '#fef3c7', text: '#92400e' },
    Money: { bg: '#d1fae5', text: '#065f46' },
    Number: { bg: '#ede9fe', text: '#5b21b6' },
  };

  return (
    <div style={{ padding: 16, fontFamily: "'Segoe UI', sans-serif" }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 28, marginBottom: 4 }}>🚀</div>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Artemis Generation</h2>
        <p style={{ margin: '4px 0 0', fontSize: 11, color: '#64748b' }}>
          Convert any document into a template
        </p>
      </div>

      {/* ── Analyzing State ── */}
      {analyzing && (
        <div style={{ textAlign: 'center', padding: '30px 0' }}>
          <div style={{
            width: 36, height: 36, margin: '0 auto 12px',
            border: '3px solid #e2e8f0', borderTopColor: '#0d9488',
            borderRadius: '50%', animation: 'spin 0.8s linear infinite',
          }} />
          <p style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>Analyzing document...</p>
          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>AI is identifying dynamic fields</p>
        </div>
      )}

      {/* ── Error State ── */}
      {error && !analyzing && (
        <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, marginBottom: 12 }}>
          <p style={{ fontSize: 12, color: '#991b1b', margin: 0 }}>❌ {error}</p>
        </div>
      )}

      {/* ── Retry button ── */}
      {!analyzing && step === 'analyze' && (
        <button onClick={analyzeDocument} style={{
          width: '100%', padding: '10px', background: '#0d9488', color: 'white',
          border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>
          🔄 Analyze Again
        </button>
      )}

      {/* ── Review State ── */}
      {step === 'review' && (
        <div>
          {/* Stats bar */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '8px 10px', background: '#f8fafc', borderRadius: 8, marginBottom: 10, fontSize: 11,
          }}>
            <span><strong>{suggestions.length}</strong> found</span>
            <span style={{ color: '#16a34a' }}>✓ {accepted.length}</span>
            <span style={{ color: '#94a3b8' }}>⏳ {pending.length}</span>
            <span style={{ color: '#dc2626' }}>✗ {rejected.length}</span>
          </div>

          {/* Bulk actions */}
          {pending.length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <button onClick={acceptAll} style={{
                flex: 1, padding: '6px', background: '#f0fdf4', border: '1px solid #bbf7d0',
                borderRadius: 6, fontSize: 11, fontWeight: 600, color: '#166534', cursor: 'pointer',
              }}>
                ✓ Accept All ({pending.length})
              </button>
              <button onClick={rejectAll} style={{
                flex: 1, padding: '6px', background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: 6, fontSize: 11, fontWeight: 600, color: '#991b1b', cursor: 'pointer',
              }}>
                ✗ Reject All
              </button>
            </div>
          )}

          {/* Suggestions list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 400, overflowY: 'auto' }}>
            {suggestions.map((s) => {
              const tc = typeColors[s.suggestedType] || typeColors.Text;
              const dimmed = s.status === 'rejected';
              return (
                <div key={s.id} style={{
                  padding: '10px',
                  borderRadius: 8,
                  border: s.status === 'accepted' ? '2px solid #bbf7d0' : s.status === 'rejected' ? '1px solid #e5e7eb' : '1px solid #e2e8f0',
                  background: s.status === 'accepted' ? '#f0fdf4' : s.status === 'rejected' ? '#f9fafb' : 'white',
                  opacity: dimmed ? 0.5 : 1,
                }}>
                  {/* Original text → Placeholder */}
                  <div style={{ fontSize: 12, marginBottom: 6 }}>
                    <span style={{ color: '#dc2626', textDecoration: 'line-through', fontStyle: 'italic' }}>
                      "{s.originalText}"
                    </span>
                    <span style={{ color: '#94a3b8', margin: '0 6px' }}>→</span>
                    {s.editing ? (
                      <span style={{ display: 'inline-flex', gap: 4 }}>
                        <input
                          value={s.suggestedKey}
                          onChange={(e) => updateSuggestion(s.id, { suggestedKey: e.target.value })}
                          style={{ width: 100, padding: '2px 4px', fontSize: 11, border: '1px solid #cbd5e1', borderRadius: 4 }}
                        />
                        <select
                          value={s.suggestedType}
                          onChange={(e) => updateSuggestion(s.id, { suggestedType: e.target.value })}
                          style={{ padding: '2px 4px', fontSize: 11, border: '1px solid #cbd5e1', borderRadius: 4 }}
                        >
                          <option>Text</option>
                          <option>Date</option>
                          <option>Money</option>
                          <option>Number</option>
                        </select>
                        <button onClick={() => updateSuggestion(s.id, { editing: false })}
                          style={{ padding: '2px 6px', fontSize: 10, background: '#0d9488', color: 'white', border: 'none', borderRadius: 3, cursor: 'pointer' }}>
                          Done
                        </button>
                      </span>
                    ) : (
                      <span>
                        <strong style={{ color: '#0d9488' }}>{`{${s.suggestedKey}}`}</strong>
                        <span style={{
                          marginLeft: 6, padding: '1px 5px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                          background: tc.bg, color: tc.text,
                        }}>
                          {s.suggestedType}
                        </span>
                        {s.existingMatch ? (
                          <span style={{
                            marginLeft: 4, padding: '1px 5px', borderRadius: 3, fontSize: 9, fontWeight: 700,
                            background: '#dbeafe', color: '#1d4ed8',
                          }}>
                            EXISTING
                          </span>
                        ) : (
                          <span style={{
                            marginLeft: 4, padding: '1px 5px', borderRadius: 3, fontSize: 9, fontWeight: 700,
                            background: '#fef3c7', color: '#92400e',
                          }}>
                            NEW
                          </span>
                        )}
                      </span>
                    )}
                  </div>

                  {/* Reason */}
                  <p style={{ fontSize: 10, color: '#64748b', margin: '0 0 6px', lineHeight: 1.3 }}>{s.reason}</p>

                  {/* Actions */}
                  {!dimmed && !s.editing && (
                    <div style={{ display: 'flex', gap: 4 }}>
                      {s.status !== 'accepted' && (
                        <button onClick={() => acceptAndPlace(s)}
                          style={{ padding: '3px 8px', fontSize: 10, fontWeight: 600, background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 4, cursor: 'pointer' }}>
                          ✓ Accept
                        </button>
                      )}
                      {s.status === 'accepted' && (
                        <span style={{ padding: '3px 8px', fontSize: 10, fontWeight: 600, color: '#166534' }}>
                          ✓ Placed
                        </span>
                      )}
                      {!s.existingMatch && s.status !== 'accepted' && (
                        <button onClick={() => updateSuggestion(s.id, { editing: true })}
                          style={{ padding: '3px 8px', fontSize: 10, fontWeight: 600, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer' }}>
                          ✎ Edit
                        </button>
                      )}
                      {s.status !== 'rejected' && s.status !== 'accepted' && (
                        <button onClick={() => updateSuggestion(s.id, { status: 'rejected' })}
                          style={{ padding: '3px 8px', fontSize: 10, fontWeight: 600, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer' }}>
                          ✗ Delete
                        </button>
                      )}
                    </div>
                  )}
                  {dimmed && (
                    <button onClick={() => updateSuggestion(s.id, { status: 'pending' })}
                      style={{ padding: '3px 8px', fontSize: 10, color: '#64748b', background: 'none', border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer' }}>
                      Restore
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Status when all reviewed */}
          {pending.length === 0 && accepted.length > 0 && (
            <div style={{
              padding: 12, marginTop: 12, background: '#f0fdf4', border: '1px solid #bbf7d0',
              borderRadius: 8, textAlign: 'center',
            }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#166534', margin: 0 }}>
                ✅ {accepted.length} placeholder{accepted.length > 1 ? 's' : ''} placed in the document
              </p>
              <p style={{ fontSize: 11, color: '#475569', margin: '6px 0 0' }}>
                Click <strong>Save Template</strong> in the ribbon to save as a template.
              </p>
            </div>
          )}

          {/* Re-analyze */}
          <button onClick={analyzeDocument} style={{
            width: '100%', padding: '8px', marginTop: 8,
            background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 6,
            fontSize: 11, color: '#64748b', cursor: 'pointer',
          }}>
            🔄 Re-analyze Document
          </button>
        </div>
      )}

      {/* ── Applying State ── */}
      {step === 'applying' && (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
            Applying placeholders... ({applyProgress}/{applyTotal})
          </p>
          <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden', margin: '12px 0' }}>
            <div style={{
              height: '100%', width: `${(applyProgress / applyTotal) * 100}%`,
              background: '#0d9488', borderRadius: 3, transition: 'width 0.3s',
            }} />
          </div>
          <p style={{ fontSize: 11, color: '#64748b' }}>Replacing text with content controls...</p>
        </div>
      )}

      {/* ── Done State ── */}
      {step === 'done' && (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
          <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#166534' }}>Template Created!</h3>
          <p style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
            {accepted.length} placeholder{accepted.length > 1 ? 's' : ''} applied to the document.
            {accepted.filter(s => s.existingMatch).length > 0 && (
              <><br/>{accepted.filter(s => s.existingMatch).length} reused from existing catalog.</>
            )}
            {accepted.filter(s => !s.existingMatch).length > 0 && (
              <><br/>{accepted.filter(s => !s.existingMatch).length} new placeholder{accepted.filter(s => !s.existingMatch).length > 1 ? 's' : ''} registered in catalog.</>
            )}
          </p>
          <p style={{ fontSize: 11, color: '#475569', marginBottom: 16 }}>
            Next: Click <strong>Save Template</strong> in the ribbon to save this as a template.
          </p>
          <button onClick={() => { hasRun.current = false; setStep('analyze'); setSuggestions([]); analyzeDocument(); }}
            style={{
              width: '100%', padding: '8px', background: 'transparent',
              border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 11, color: '#64748b', cursor: 'pointer',
            }}>
            🔄 Analyze Again
          </button>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
